<template>
  <div v-show="agentSetup.currentPage === 2" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm min-h-[40vh] animate-fade-in">
    <div class="mb-6" id="tabTitle">
      <h1 class="text-2xl font-semibold tracking-tight text-base-content" :style="agentSetup.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ agentSetup.t('aiChatAgentSetup.tabs.agentPrompt', 'Agent Prompt') }}</h1>
      <p class="mt-2 text-sm text-base-content/70" :style="agentSetup.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ agentSetup.t('aiChatAgentSetup.agentPrompt.description', 'Design the agent prompt in a visual markdown editor. The saved prompt stays in Markdown.') }}</p>
    </div>

    <div class="mb-4 flex items-center justify-between">
      <div class="text-xs text-base-content/60" :style="agentSetup.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ agentSetup.t('aiChatAgentSetup.agentPrompt.markdownSaved', 'Saved as Markdown') }}</div>
      <div class="text-xs text-base-content/60" :style="agentSetup.isRtl === true ? { direction: 'rtl', textAlign: 'left' } : { direction: 'ltr', textAlign: 'right' }">{{ promptLength }}/200000</div>
    </div>

    <PromptMarkdownEditor
      :model-value="agentSetup.agentDescription"
      :placeholder="agentSetup.t('aiChatAgentSetup.agentPrompt.placeholder', 'Write the full prompt here. Markdown like headings, lists, links, quotes, and code blocks is supported.')"
      :direction="agentSetup.isRtl === true ? 'rtl' : 'ltr'"
      :error="agentSetup.agentDescriptionError === true"
      :labels="promptEditorLabels"
      @update:model-value="updatePromptValue"
    />

    <div v-if="showPromptWarning === true" class="alert alert-warning mt-4">
      <span>{{ agentSetup.t('aiChatAgentSetup.agentPrompt.lengthWarning', 'More than 4000 chars can slow the agent down. We recommend keeping the prompt shorter when possible.') }}</span>
    </div>
    <p v-if="agentSetup.agentDescriptionError === true" class="text-error text-xs mt-2">{{ agentSetup.agentDescriptionErrorMessage }}</p>

    <div class="flex justify-between mt-6" style="position: relative;">
      <button @click="agentSetup.goNextStep(2, -1)" class="btn">
        <span v-if="agentSetup.isNavigating === 1" class="loading loading-spinner loading-sm mr-2"></span>
        {{ agentSetup.t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
      </button>
      <div class="flex items-center space-x-2" :class="agentSetup.isRtl === true ? 'space-x-reverse' : ''">
        <button v-if="agentSetup.isEditMode === true && agentSetup.uploadedFiles.length === 0" @click="agentSetup.saveAgentWithoutRedirect" class="btn btn-neutral">
          <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.save', 'Save') }}
        </button>
        <button v-if="agentSetup.isEditMode === true" @click="agentSetup.createAgent(true)" class="btn">
          <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
        </button>
        <button @click="agentSetup.goNextStep(2, 1)" class="btn">
          <span v-if="agentSetup.isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useAgentSetupContext } from '@/components/AgentSetupContext.js';
import PromptMarkdownEditor from '@/components/PromptMarkdownEditor.vue';

const agentSetup = useAgentSetupContext();

const promptLength = computed(() => {
  return typeof agentSetup.agentDescription === 'string' ? agentSetup.agentDescription.length : 0;
});

const showPromptWarning = computed(() => {
  return promptLength.value > 4000;
});

const promptEditorLabels = computed(() => {
  return {
    visualMode: agentSetup.t('aiChatAgentSetup.agentPrompt.visualMode', 'Visual'),
    textMode: agentSetup.t('aiChatAgentSetup.agentPrompt.textMode', 'Text'),
    paragraph: agentSetup.t('aiChatAgentSetup.agentPrompt.paragraph', 'Paragraph'),
    heading: agentSetup.t('aiChatAgentSetup.agentPrompt.heading', 'Heading'),
    subheading: agentSetup.t('aiChatAgentSetup.agentPrompt.subheading', 'Subheading'),
    bold: agentSetup.t('aiChatAgentSetup.agentPrompt.bold', 'Bold'),
    italic: agentSetup.t('aiChatAgentSetup.agentPrompt.italic', 'Italic'),
    link: agentSetup.t('aiChatAgentSetup.agentPrompt.link', 'Link'),
    bullets: agentSetup.t('aiChatAgentSetup.agentPrompt.bullets', 'Bullets'),
    numberedList: agentSetup.t('aiChatAgentSetup.agentPrompt.numberedList', 'Numbered list'),
    quote: agentSetup.t('aiChatAgentSetup.agentPrompt.quote', 'Quote'),
    codeBlock: agentSetup.t('aiChatAgentSetup.agentPrompt.codeBlock', 'Code block'),
    divider: agentSetup.t('aiChatAgentSetup.agentPrompt.divider', 'Divider'),
    clear: agentSetup.t('aiChatAgentSetup.agentPrompt.clear', 'Clear'),
    editLink: agentSetup.t('aiChatAgentSetup.agentPrompt.editLink', 'Edit Link'),
    linkText: agentSetup.t('aiChatAgentSetup.agentPrompt.linkText', 'Link text'),
    linkUrl: agentSetup.t('aiChatAgentSetup.agentPrompt.linkUrl', 'URL'),
    saveLink: agentSetup.t('aiChatAgentSetup.agentPrompt.saveLink', 'Save'),
    cancel: agentSetup.t('aiChatAgentSetup.buttons.cancel', 'Cancel'),
    removeLink: agentSetup.t('aiChatAgentSetup.agentPrompt.removeLink', 'Remove link')
  };
});

function updatePromptValue(value) {
  agentSetup.agentDescription = typeof value === 'string' ? value : '';
  agentSetup.handleAgentDescriptionInput();
}
</script>
